import Vue from "vue";
import Vuex from "vuex";
import uniqueId from "./util/uniqueId";
import { API_BASE } from "./util/constants";
import {redirectToLogin} from "./oauth"
import router from "./router";
import {
  AUTHENTICATION_REQUIRED_MESSAGE,
  AUTHENTICATION_REQUIRED_MESSAGE_KEY,
  AUTHENTICATION_CHECK_ERROR_MESSAGE_KEY,
} from "./util/messageKeys";

Vue.use(Vuex);

const PERIODIC_CHECK_INTERVAL = 60000;

// assume JWT is invalid slightly before it actually expires so that users
// aren't likely to be interrupted half way through an operation
const MIN_JWT_VALIDITY = 90000;

// If the token has *actually* expired, then we'll need to redirect to a full login
// So give ourselves a two-second - if there's less than that until expiry, just send the user
// straight to the login screen
const OAUTH_REDIRECT_TIME_BUFFER = 2000;
let initPagePromise = null;

function getValidStoredAuth() {
  const token = localStorage.getItem("token");
  const expiresAt = Number(localStorage.getItem("expiresAt"));
  if (!token || !expiresAt || (Date.now() + MIN_JWT_VALIDITY) >= expiresAt) {
    return null;
  }
  return { token, expiresAt };
}

let store = new Vuex.Store({
  state: {
    auth: {
      token: null,
      userData: null,
      expiresAt: null,
      loginRedirectStarted: false,
    },
    info: {
      oauthUrl: null,
      environment: null,
    },
    maintenanceMode: {
      enabled: false,
      updatedOn: null,
      updatedBy: null,
      scheduledAt: null,
      initialized: false,
    },
    systemBanner: {
      enabled: false,
      level: "info",
      message: null,
      updatedOn: null,
      updatedBy: null,
      initialized: false,
    },
    loader: [],
    crumbs: [],
    messages: [],
    timer: null,
  },
  mutations: {
    SET_AUTH_DATA: (state, { token, userData, expiresAt }) => {
      state.auth.token = token;
      state.auth.userData = userData;
      state.auth.expiresAt = expiresAt;
      if (token && userData) {
        state.auth.loginRedirectStarted = false;
      }
    },
    SET_LOGIN_REDIRECT_STARTED: (state, started) => {
      state.auth.loginRedirectStarted = started;
    },
    SET_INFO_DATA: (state, { oauthUrl, environment }) => {
      state.info.oauthUrl = oauthUrl;
      state.info.environment = environment;
    },
    SET_MAINTENANCE_MODE: (state, payload = {}) => {
      state.maintenanceMode.enabled = payload.enabled ?? false;
      state.maintenanceMode.updatedOn = payload.updatedOn ?? null;
      state.maintenanceMode.updatedBy = payload.updatedBy ?? null;
      state.maintenanceMode.scheduledAt = payload.scheduledAt ?? null;
      state.maintenanceMode.initialized = payload.initialized ?? false;
    },
    SET_SYSTEM_BANNER: (state, payload = {}) => {
      state.systemBanner.enabled = payload.enabled ?? false;
      state.systemBanner.level = payload.level ?? "info";
      state.systemBanner.message = payload.message ?? null;
      state.systemBanner.updatedOn = payload.updatedOn ?? null;
      state.systemBanner.updatedBy = payload.updatedBy ?? null;
      state.systemBanner.initialized = payload.initialized ?? false;
    },
    BEGIN_LOAD: (state, id) => {
      state.loader.push(id);
    },
    END_LOAD: (state, id) => {
      const pos = state.loader.indexOf(id);
      if (pos !== -1) {
        state.loader.splice(pos, 1);
      }
    },
    SET_BREADCRUMBS: (state, crumbs) => {
      state.crumbs.splice(0, state.crumbs.length, ...crumbs);
    },
    ADD_MESSAGE: (state, message) => {
      state.messages.push(message);
    },
    REMOVE_MESSAGE: (state, id) => {
      const index = state.messages.findIndex((message) => (message.id === id));
      if (index !== -1) {
        state.messages.splice(index, 1);
      }
    },
    REMOVE_MESSAGES_BY_KEY: (state, key) => {
      state.messages = state.messages.filter((message) => message.key !== key);
    },
    CLEAR_MESSAGES: (state) => {
      state.messages = [];
    },
  },
  actions: {
    initPage({ state, dispatch }) {
      if (initPagePromise) {
        return initPagePromise;
      }
      initPagePromise = (async () => {
        await dispatch('syncStoredAuth');
        if (!state.info.environment) {
          await dispatch('loadInfo');
        }
        await dispatch('loadMaintenanceMode');
        await dispatch('loadSystemBanner');
        if (!state.timer) {
          state.timer = setInterval(() => dispatch('periodicChecks'), PERIODIC_CHECK_INTERVAL);
        }
      })();
      return initPagePromise.finally(() => {
        initPagePromise = null;
      });
    },
    async syncStoredAuth({ state, dispatch }) {
      const storedAuth = getValidStoredAuth();
      if (!storedAuth) {
        return false;
      }
      if (
        state.auth.token === storedAuth.token &&
        Number(state.auth.expiresAt) === storedAuth.expiresAt &&
        state.auth.userData
      ) {
        return true;
      }
      try {
        await dispatch('loadUserData', { ...storedAuth, expiresIn: null });
        return true;
      } catch (error) {
        if (error.status === 401) {
          await dispatch('notifyAuthenticationRequired');
          await dispatch('clearUserToken');
          return false;
        }
        await dispatch('notifyAuthenticationCheckError');
        throw error;
      }
    },
    async loadUserData({ commit }, { token, expiresIn, expiresAt }) {
      if (!expiresAt) {
        expiresAt = Date.now() + (expiresIn * 1000);
      }
      const response = await fetch(API_BASE + '/user/info', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const error = new Error(`Unable to load current user (${response.status})`);
        error.status = response.status;
        throw error;
      }
      const userData = await response.json();
      localStorage.removeItem("loggedOut");
      localStorage.setItem("token", token);
      localStorage.setItem("expiresAt", expiresAt + "");
      commit("SET_AUTH_DATA", { token: token, userData, expiresAt });
      commit("REMOVE_MESSAGES_BY_KEY", AUTHENTICATION_REQUIRED_MESSAGE_KEY);
    },
    async loadInfo({ commit }) {
      const response = await fetch(API_BASE.replace("/api/v2", "") + '/actuator/info', {});
      const userData = await response.json();
      commit("SET_INFO_DATA", { oauthUrl: userData.oauthurl, environment: userData.environment });
    },
    clearUserToken({ commit }) {
      localStorage.removeItem("token");
      localStorage.removeItem("expiresAt");
      commit("SET_AUTH_DATA", { token: null, userData: null, expiresAt: null });
    },
    beginLoginRedirect({ state, commit }) {
      if (state.auth.loginRedirectStarted) {
        return false;
      }
      commit("SET_LOGIN_REDIRECT_STARTED", true);
      return true;
    },
    notifyAuthenticationRequired({ state, dispatch }) {
      if (state.messages.some(({ key }) => key === AUTHENTICATION_REQUIRED_MESSAGE_KEY)) {
        return;
      }
      return dispatch('addMessage', {
        type: 'error',
        label: AUTHENTICATION_REQUIRED_MESSAGE,
        expiryTime: 5000,
        key: AUTHENTICATION_REQUIRED_MESSAGE_KEY,
      });
    },
    notifyAuthenticationCheckError({ state, dispatch }) {
      if (state.messages.some(({ key }) => key === AUTHENTICATION_CHECK_ERROR_MESSAGE_KEY)) {
        return;
      }
      return dispatch('addMessage', {
        type: 'error',
        label: 'Unable to verify your session. Please try again.',
        expiryTime: 5000,
        key: AUTHENTICATION_CHECK_ERROR_MESSAGE_KEY,
      });
    },
    async periodicChecks({ state, dispatch }) {
      let expiresAt = state.auth.expiresAt;
      if (expiresAt) {
        // check if login token has expired to save users from getting stuck
        if (Date.now() + OAUTH_REDIRECT_TIME_BUFFER > expiresAt) {
          await dispatch('notifyAuthenticationRequired');
          redirectToLogin({originalRoute: router.currentRoute.fullPath})
        }
        else if (Date.now() + MIN_JWT_VALIDITY > expiresAt) {
          redirectToLogin({silent: true, originalRoute: router.currentRoute.fullPath})
        }
      }
      await dispatch('loadMaintenanceMode');
      await dispatch('loadSystemBanner');
    },
    beginLoad({ commit }, id) {
      commit('BEGIN_LOAD', id);
    },
    endLoad({ commit }, id) {
      commit('END_LOAD', id);
    },
    setBreadcrumbs({ commit }, crumbs) {
      commit('SET_BREADCRUMBS', crumbs);
    },
    addMessage({ dispatch, commit }, message) {
      const id = uniqueId();
      commit('ADD_MESSAGE', { ...message, id });
      setTimeout(() => dispatch('removeMessage', id), message.expiryTime);
      return id;
    },
    removeMessage({ commit }, id) {
      commit('REMOVE_MESSAGE', id);
    },
    async loadMaintenanceMode({ state, commit }) {
      if (!state.auth.token) {
        commit('SET_MAINTENANCE_MODE', { enabled: false, updatedOn: null, updatedBy: null, scheduledAt: null, initialized: true });
        return;
      }
      try {
        const response = await fetch(API_BASE + '/maintenance-mode', {
          headers: {
            'Authorization': `Bearer ${state.auth.token}`,
          },
        });
        if (!response.ok) {
          commit('SET_MAINTENANCE_MODE', {
            enabled: state.maintenanceMode.enabled,
            updatedOn: state.maintenanceMode.updatedOn,
            updatedBy: state.maintenanceMode.updatedBy,
            scheduledAt: state.maintenanceMode.scheduledAt,
            initialized: true,
          });
          return;
        }
        const previousValue = state.maintenanceMode.enabled;
        const data = await response.json();
        commit('SET_MAINTENANCE_MODE', { ...data, initialized: true });
        if (data.enabled && !previousValue && router.currentRoute.name !== "maintenance-mode") {
          router.push({ name: "maintenance-mode" }).catch(() => {});
        }
      } catch (e) {
        commit('SET_MAINTENANCE_MODE', {
          enabled: state.maintenanceMode.enabled,
          updatedOn: state.maintenanceMode.updatedOn,
          updatedBy: state.maintenanceMode.updatedBy,
          scheduledAt: state.maintenanceMode.scheduledAt,
          initialized: true,
        });
      }
    },
    async loadSystemBanner({ state, commit }) {
      if (!state.auth.token) {
        commit('SET_SYSTEM_BANNER', { enabled: false, level: "info", message: null, updatedOn: null, updatedBy: null, initialized: true });
        return;
      }
      try {
        const response = await fetch(API_BASE + '/system-banner', {
          headers: {
            'Authorization': `Bearer ${state.auth.token}`,
          },
        });
        if (!response.ok) {
          commit('SET_SYSTEM_BANNER', {
            enabled: state.systemBanner.enabled,
            level: state.systemBanner.level,
            message: state.systemBanner.message,
            updatedOn: state.systemBanner.updatedOn,
            updatedBy: state.systemBanner.updatedBy,
            initialized: true,
          });
          return;
        }
        const data = await response.json();
        commit('SET_SYSTEM_BANNER', { ...data, initialized: true });
      } catch (e) {
        commit('SET_SYSTEM_BANNER', {
          enabled: state.systemBanner.enabled,
          level: state.systemBanner.level,
          message: state.systemBanner.message,
          updatedOn: state.systemBanner.updatedOn,
          updatedBy: state.systemBanner.updatedBy,
          initialized: true,
        });
      }
    },
    async setMaintenanceMode({ state, dispatch }, { enabled, scheduledAt }) {
      if (!state.auth.token) {
        return;
      }
      const response = await fetch(API_BASE + '/maintenance-mode', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${state.auth.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled, scheduledAt }),
      });
      if (!response.ok) {
        throw new Error("Failed to update maintenance mode");
      }
      await dispatch('loadMaintenanceMode');
    },
    async setSystemBanner({ state, dispatch }, { enabled, level, message }) {
      if (!state.auth.token) {
        return;
      }
      const response = await fetch(API_BASE + '/system-banner', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${state.auth.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled, level, message }),
      });
      if (!response.ok) {
        throw new Error("Failed to update system banner");
      }
      await dispatch('loadSystemBanner');
    },
  },
});

export default () => store;
